/**
 * Which extensions a contract action may offer (EB-4209 / EB-4210).
 *
 * Old CMT keeps these rules in `contractRevisions.js` (`excludeSystemExtensions`) and
 * `addingExtension.js` (`excludeBookedExtensions`). They are replicated here as pure
 * functions so both dialogs share one source of truth.
 *
 * Two deliberate deviations from old CMT, both found in the EB-4209 review:
 * - An empty catalog still leaves `TarifErw2` removable here. In old CMT the `TarifErw2`
 *   clause sits inside the loop over the catalog, so an empty catalog drops every option
 *   including `TarifErw2` — an artefact of the loop shape, not of the rule.
 * - The Add list is fed from the price list + tariff catalog (`onlyShowableOnFe=true`),
 *   whereas old CMT feeds it from `plans/{planId}/extensions?system=true`. Same
 *   subtraction rule, different source list; set equality is unverified (probed equal for
 *   plan 70325 / price list 180 only, and the contract API is not available to check).
 */

/**
 * The 24h / "Verlängerte Geschäftszeiten" extension (old CMT `Constants.SERVICE_24H`).
 * It is the hard-coded exception to the system-extension rule: always removable, even
 * when the price list's FE-showable catalog would exclude it — and the only extension
 * that offers "remove just from this contract".
 */
export const SERVICE_24H = 'TarifErw2';

/** Minimal shape both the booked-extension and the catalog DTO satisfy. */
interface HasExtensionName {
  extensionPriceInfo: { name: string };
}

/**
 * Booked extensions that may be removed: those the price list + tariff catalog still
 * offers, plus {@link SERVICE_24H} unconditionally. Everything else is a system
 * extension the operator must not end from the contract UI.
 */
export function removableExtensions<T extends HasExtensionName>(
  booked: readonly T[],
  catalog: readonly HasExtensionName[],
): T[] {
  const catalogNames = new Set(catalog.map((e) => e.extensionPriceInfo.name));
  return booked.filter(
    (e) => catalogNames.has(e.extensionPriceInfo.name) || e.extensionPriceInfo.name === SERVICE_24H,
  );
}

/**
 * Catalog entries that may be added: everything not already booked on the contract
 * for the selected start date. Booking the same extension twice is not allowed.
 */
export function addableExtensions<T extends HasExtensionName>(
  catalog: readonly T[],
  booked: readonly HasExtensionName[],
): T[] {
  const bookedNames = new Set(booked.map((e) => e.extensionPriceInfo.name));
  return catalog.filter((e) => !bookedNames.has(e.extensionPriceInfo.name));
}
